interface Offsets {
  start: number;
  end: number;
}

interface HastText {
  type: 'text';
  value: string;
  position?: { start: { offset?: number }; end: { offset?: number } };
}

interface HastElement {
  type: 'element';
  tagName: string;
  properties: Record<string, unknown>;
  children: HastNode[];
  position?: HastText['position'];
}

interface HastRaw extends Omit<HastText, 'type'> {
  type: 'raw';
}

type HastNode = HastText | HastRaw | HastElement | { type: string };

interface HastParent {
  type: string;
  children: HastNode[];
}

export interface RehypeChatTextOptions {
  /** Source characters before this offset are spoken. Undefined means no split. */
  spokenChars?: number;
  /** Wrap the message in curly quotes. */
  quoted?: boolean;
}

export const CHAT_MARKDOWN_QUOTE_CLASS = 'chat-markdown-quote';

const OPEN_QUOTE = '“';
const CLOSE_QUOTE = '”';

const isText = (node: HastNode): node is HastText => node.type === 'text';
const isRaw = (node: HastNode): node is HastRaw => node.type === 'raw';
const isElement = (node: HastNode): node is HastElement => node.type === 'element';

const text = (value: string): HastText => ({ type: 'text', value });

const element = (tagName: string, properties: Record<string, unknown>, children: HastNode[]): HastElement => ({
  type: 'element',
  tagName,
  properties,
  children,
});

function offsetsOf(node: HastText | HastElement): Offsets | null {
  const start = node.position?.start.offset;
  const end = node.position?.end.offset;
  return typeof start === 'number' && typeof end === 'number' ? { start, end } : null;
}

function toText(node: HastNode): string {
  if (isText(node)) return node.value;
  if (isElement(node)) return node.children.map(toText).join('');
  return '';
}

function withBreaks(value: string): HastNode[] {
  return value.split('\n').flatMap((line, i) => {
    const nodes: HastNode[] = i > 0 ? [element('br', {}, [])] : [];
    if (line) nodes.push(text(line));
    return nodes;
  });
}

function splitSpoken(value: string, range: Offsets, spokenChars: number): Array<[string, boolean]> {
  if (spokenChars >= range.end) return [[value, true]];
  if (spokenChars <= range.start) return [[value, false]];
  const k = spokenChars - range.start;
  return [
    [value.slice(0, k), true],
    [value.slice(k), false],
  ];
}

function quote(value: string): HastElement {
  return element('span', { className: [CHAT_MARKDOWN_QUOTE_CLASS], dataTestid: 'ari.chat-markdown.quote' }, [
    text(value),
  ]);
}

function addQuotes(tree: HastParent): void {
  const blocks = tree.children.filter(isElement);
  if (blocks.length === 0) return;

  const first = blocks[0];
  const last = blocks[blocks.length - 1];
  if (first.tagName === 'p') first.children.unshift(quote(OPEN_QUOTE));
  else tree.children.splice(tree.children.indexOf(first), 0, element('p', {}, [quote(OPEN_QUOTE)]));
  if (last.tagName === 'p') last.children.push(quote(CLOSE_QUOTE));
  else tree.children.splice(tree.children.indexOf(last) + 1, 0, element('p', {}, [quote(CLOSE_QUOTE)]));
}

/**
 * Wrap text in spoken / unspoken spans at `spokenChars`, keep soft line breaks,
 * and add the quote marks. Runs after rehype-highlight so code keeps its colours.
 */
export function rehypeChatText({ spokenChars, quoted }: RehypeChatTextOptions) {
  return (tree: { type: string }, file: { value: unknown }) => {
    const source = String(file.value);
    // Highlighted code text has no position, so it counts on from the last known offset.
    let offset = 0;

    const locate = ({ start, end }: Offsets, value: string) =>
      start + Math.max(source.slice(start, end).indexOf(value), 0);

    const rangeOf = (node: HastText): Offsets => {
      const offsets = offsetsOf(node);
      if (offsets) {
        const start = locate(offsets, node.value);
        offset = offsets.end;
        return { start, end: Math.min(offsets.end, start + node.value.length) };
      }
      const start = offset;
      offset += node.value.length;
      return { start, end: offset };
    };

    const transformText = (node: HastText, inPre: boolean): HastNode[] => {
      if (!inPre && node.value.trim() === '') return [node];
      const render = (value: string) => (inPre ? [text(value)] : withBreaks(value));
      if (spokenChars === undefined) return render(node.value);

      return splitSpoken(node.value, rangeOf(node), spokenChars).map(([value, spoken]) =>
        element('span', { dataSpoken: String(spoken) }, render(value))
      );
    };

    const transformChildren = (parent: HastParent, inPre: boolean): void => {
      const next: HastNode[] = [];
      for (const child of parent.children) {
        // react-markdown turns raw HTML into text only after the rehype plugins run.
        if (isText(child) || isRaw(child)) {
          next.push(...transformText({ ...child, type: 'text' }, inPre));
          continue;
        }
        if (isElement(child)) {
          const offsets = offsetsOf(child);
          if (offsets && child.tagName === 'pre') offset = locate(offsets, toText(child));
          transformChildren(child, inPre || child.tagName === 'pre');
          if (offsets) offset = Math.max(offset, offsets.end);
        }
        next.push(child);
      }
      parent.children = next;
    };

    const root = tree as HastParent;
    transformChildren(root, false);
    if (quoted) addQuotes(root);
  };
}
